import { ComponentType, ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { SvgIconProps } from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";

/**
 * What a section of the panel is: settled fact (the sign-offs, the recorded outcome) or the
 * irreversible action (the closing form itself).
 */
type ClosingSectionAccent = "success" | "error";

interface ClosingSectionBoxProps {
  title: string;
  icon: ComponentType<SvgIconProps>;
  accent: ClosingSectionAccent;
  children: ReactNode;
}

/**
 * The shell every ปิดศูนย์เซล section sits in: an outlined card under a coloured heading.
 *
 * Outlined rather than tinted. The real content of the screen is the report table above this
 * panel, and two filled colour blocks under it took the eye off it — the colour survives in
 * the border and the heading icon, which is enough to tell the two sections apart. Both are
 * taken from the theme's semantic palette, so they follow it if it is ever retuned.
 *
 * The box carries no background of its own and reads on whatever surface it is dropped on.
 * Height is left to the caller: the panel lays the sections out in a grid, which levels them.
 */
export default function ClosingSectionBox({
  title,
  icon: Icon,
  accent,
  children,
}: ClosingSectionBoxProps) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: `${accent}.main`,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
        <Icon fontSize="small" color={accent} />
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}
